import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/layouts/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Construction, ArrowLeft } from "lucide-react";

// A-48: none of the six routes that render this screen (`/instagram-admin`, `/tiktok-admin`,
// `/facebook-admin`, `/phone-admin`, `/sms-admin`, `/email-admin`) actually carries a `:channel`
// route param, so `useParams` always read `undefined` and every one of them fell back to the
// generic default-channel copy. The channel name is real information the route path already
// has -- this reads it from the URL itself instead of a param that was never wired up.
const CHANNEL_LABELS: Record<string, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  phone: 'Phone',
  sms: 'SMS',
  email: 'Email',
};

export default function ComingSoon() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const slug = location.pathname.replace(/^\//, '').replace(/-admin\/?$/, '');
  const channelName = CHANNEL_LABELS[slug] ?? t('comingSoonDefaultChannel');

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Construction className="h-8 w-8 text-primary" />
                </div>
              </div>
              <CardTitle className="text-center text-3xl">
                {t('comingSoonTitle', { channel: channelName })}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-center text-muted-foreground text-lg">
                {t('comingSoonMessage', { channel: channelName })}
              </p>

              {/* A-50: four empty adjectives ("seamless integration", "AI-powered automation")
                  replaced with the one honest fact — there is no date yet. */}
              <p className="text-center text-sm text-muted-foreground">
                {t('comingSoonNoDateYet')}
              </p>

              {/* A-49: "Contact your account manager" named nobody a self-signup merchant has.
                  This is the same real contact every payment result screen in this product
                  already points to. */}
              <p className="text-center text-sm text-muted-foreground">
                {t('comingSoonNotification', { channel: channelName })}{' '}
                <a href="https://okostec.ai" target="_blank" rel="noreferrer" className="underline">
                  okostec.ai
                </a>
              </p>

              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('backToDashboard')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
